import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import {
  Box,
  Collapse,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Fade,
  TextField,
  alpha,
  useTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  Print as PrintIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PICustomerSection from './pi-creation-components/PICustomerSection';
import PILineItemsSection from './pi-creation-components/PILineItemsSection';
import PISummarySection from './pi-creation-components/PISummarySection';
import PITermsConditionsSection from './pi-creation-components/PITermsConditionsSection';
import PIPrintable from './pi-creation-components/PIPrintable';
import { calculatePOTotals, getTodayString, formatCompanyAddress } from './pi-creation-components/utils';

const ACCORDION_KEY = 'pi-creation-accordion';

const getInitialAccordionState = () => {
  try {
    const stored = localStorage.getItem(ACCORDION_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const CollapsibleSection = ({ title, expanded, onToggle, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mt: 2 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.25,
          px: 2.5,
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: expanded ? '12px 12px 0 0' : '12px',
          background: 'white',
          border: `1px solid ${alpha(theme.palette.grey[300], 0.9)}`,
          borderBottom: expanded
            ? `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
            : `1px solid ${alpha(theme.palette.grey[300], 0.9)}`,
          boxShadow: expanded
            ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`
            : `0 1px 4px ${alpha(theme.palette.grey[400], 0.15)}`,
          transition: 'box-shadow 0.2s, border-radius 0.2s',
          '&:hover': {
            boxShadow: `0 2px 10px ${alpha(theme.palette.primary.main, 0.12)}`,
            borderColor: alpha(theme.palette.primary.main, 0.3),
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: expanded ? 'primary.main' : alpha(theme.palette.grey[400], 0.8),
            transition: 'background-color 0.2s',
            flexShrink: 0,
          }} />
          <Typography
            fontWeight={500}
            sx={{
              fontSize: '0.9rem',
              color: expanded ? 'primary.main' : 'text.secondary',
              transition: 'color 0.2s',
            }}
          >
            {title}
          </Typography>
        </Box>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: expanded ? 'primary.main' : 'text.disabled',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s, color 0.2s',
          }}
        />
      </Box>
      <Collapse in={expanded}>
        <Box
          sx={{
            '& > *:first-of-type': {
              mt: '0 !important',
              borderRadius: '0 0 12px 12px !important',
              borderTop: 'none !important',
              animation: 'none !important',
            }
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

const PICreation = ({ purchaseOrder: propPurchaseOrder, onUpdate: propOnUpdate, currentCompany }) => {
  const { companyId, poId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const printComponentRef = useRef(null);

  // Determine if we're in edit mode or have a parent PI
  const hasParentPO = propPurchaseOrder && propPurchaseOrder.id;
  const [isEdit, setIsEdit] = useState(poId && poId !== 'new');
  
  // State
  const [purchaseOrder, setPurchaseOrder] = useState({
    po_number: propPurchaseOrder?.customer_po_number || '',
    po_date: getTodayString(),
    po_status: 'draft',
    customer_id: propPurchaseOrder?.customer_id || '',
    customer_name: propPurchaseOrder?.customer_name || '',
    customer_company: propPurchaseOrder?.customer_company || '',
    customer_po_number: propPurchaseOrder?.customer_po_number || '',
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    billing_country: '',
    shipping_street: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    shipping_country: '',
    same_as_billing: false,
    requested_delivery_date: propPurchaseOrder?.requested_delivery_date || '',
    subtotal: 0,
    discount_percent: 0,
    discount_amount: 0,
    discount_type: 'percent',
    tax_rate: 0,
    tax_amount: 0,
    shipping_fee: 0,
    handling_fee: 0,
    other_fees: 0,
    total_before_credit: 0,
    total_amount: 0,
    amount_due: 0,
    price_list_id: propPurchaseOrder?.price_list_id || null,
    price_list_name: '',
    payment_terms: propPurchaseOrder?.payment_terms || 'Net 30',
    payment_method: propPurchaseOrder?.payment_method || '',
    late_fee_rate: 0,
    early_payment_discount: 0,
    early_payment_days: 0,
    customer_notes: '',
    internal_notes: '',
    special_instructions: '',
    terms_and_conditions: '',
    prepared_by: null
  });

  const [lineItems, setLineItems] = useState([]);
  const lineItemsRef = useRef(lineItems);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [productVariants, setProductVariants] = useState({});
  const [priceLists, setPriceLists] = useState([]);
  const [inventoryLocations, setInventoryLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [company, setCompany] = useState(currentCompany || null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState({ name: '', position: '' });
  const [printOption, setPrintOption] = useState('summary'); // 'summary', 'lotBreakdown', or 'both'
  const [accordion, setAccordion] = useState(getInitialAccordionState);

  const toggleSection = useCallback((key) => {
    setAccordion(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(ACCORDION_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // Permission checks
  const canView = hasPermission('sales.purchase_orders.po_creation.view');
  const canEdit = hasPermission('sales.purchase_orders.po_creation.edit', 'write');
  const canDelete = hasPermission('sales.purchase_orders.po_creation.delete', 'delete');

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      if (!companyId) return; // Wait for companyId to be available
      
      try {
        // Always try to fetch if we have a parent PI or if we're in edit mode
        if (hasParentPO || isEdit) {
          await fetchPO();
        } else if (!propPurchaseOrder?.customer_po_number) {
          // Only generate new PI number if we don't already have one from workflow
          await generatePONumber();
        }
        await Promise.all([
          fetchCompany(),
          fetchCustomers(),
          fetchProducts(),
          fetchPriceLists(),
          fetchLocations()
        ]);
        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data: ' + err.message);
        setLoading(false);
      }
    };
    loadData();
  }, [hasParentPO, isEdit, poId, companyId]);

  // Watch for changes in propPurchaseOrder.po_number or customer_po_number and update local state
  useEffect(() => {
    if (propPurchaseOrder?.po_number || propPurchaseOrder?.customer_po_number) {
      const latestPONumber = propPurchaseOrder.po_number || propPurchaseOrder.customer_po_number;
      setPurchaseOrder(prev => {
        // Only update if it's different to avoid unnecessary re-renders
        if (prev.po_number !== latestPONumber) {
          return { ...prev, po_number: latestPONumber, customer_po_number: latestPONumber };
        }
        return prev;
      });
    }
  }, [propPurchaseOrder?.po_number, propPurchaseOrder?.customer_po_number]);

  // Fetch PI data
  const fetchPO = async () => {
    try {
      // If we have a parent PI (from workflow), fetch by purchase_order_id
      // Otherwise, fetch by po_creation id directly
      let query = supabase.from('po_creation').select('*');
      
      if (hasParentPO) {
        query = query.eq('purchase_order_id', propPurchaseOrder.id)
          .order('created_at', { ascending: false })
          .limit(1);
      } else {
        query = query.eq('id', poId);
      }

      const { data: poCreation, error: poErr } = await query.maybeSingle();

      if (poErr) throw poErr;
      
      if (poCreation) {
        // Get the latest po_number from customer_purchase_orders to ensure it's up to date
        let latestPONumber = poCreation.po_number;
        if (hasParentPO && propPurchaseOrder?.id) {
          const { data: latestPO, error: poErr2 } = await supabase
            .from('customer_purchase_orders')
            .select('po_number, customer_po_number')
            .eq('id', propPurchaseOrder.id)
            .maybeSingle();
          
          if (!poErr2 && latestPO) {
            latestPONumber = latestPO.po_number || latestPO.customer_po_number || poCreation.po_number;
          }
        }
        
        setPurchaseOrder({
          ...poCreation,
          po_number: latestPONumber,
          customer_po_number: latestPONumber
        });
        setIsEdit(true); // We found an existing record, so we're in edit mode

        // Fetch line items using the po_creation.id
        const { data: items, error: itemsErr } = await supabase
          .from('po_creation_items')
          .select('*')
          .eq('po_creation_id', poCreation.id)
          .order('line_order');

        if (itemsErr) throw itemsErr;
        if (items && items.length > 0) {
          // Rebuild descriptions like InvoiceForm.jsx - don't use stored product.description
          const rebuiltItems = items.map(item => {
            let description = '';
            if (item.lot_number) {
              // With lot number
              if (item.variant_name) {
                description = `${item.product_name || ''} - ${item.variant_name} (Lot: ${item.lot_number || 'N/A'})`;
              } else {
                description = `${item.product_name || ''} (Lot: ${item.lot_number || 'N/A'})`;
              }
            } else if (item.variant_name) {
              // With variant but no lot number
              description = `${item.product_name || ''} - ${item.variant_name}`;
            } else {
              // Base product only
              description = item.product_name || '';
            }
            return {
              ...item,
              description: description
            };
          });
          setLineItems(rebuiltItems);
        }

        // Auto-fill billing if empty
        if (poCreation.customer_id) {
          fetchAndFillCustomerBilling(poCreation.customer_id);
        }
      } else if (hasParentPO && propPurchaseOrder) {
        // No po_creation record yet, but we have parent PI data - use it!
        // Use the PI number from the workflow (customer_po_number)
        if (propPurchaseOrder.customer_po_number) {
          setPurchaseOrder(prev => ({ ...prev, po_number: propPurchaseOrder.customer_po_number }));
        } else {
          // Fallback: generate new PI number if workflow doesn't have one
          await generatePONumber();
        }

        // Auto-fill billing
        if (propPurchaseOrder.customer_id) {
          fetchAndFillCustomerBilling(propPurchaseOrder.customer_id);
        }
      }

    } catch (err) {
      console.error('Error fetching PI:', err);
      setError('Failed to load proforma invoice: ' + err.message);
    }
  };

  // Generate PI Number
  const generatePONumber = async () => {
    try {
      // Get current year and month
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const yearMonth = `${year}${month}`;

      // Count existing PIs for this company to get next number
      const { count, error } = await supabase
        .from('customer_purchase_orders')
        .select('po_number', { count: 'exact', head: true })
        .eq('company_id', companyId);

      if (error && error.code !== 'PGRST116') throw error;

      const nextNumber = (count || 0) + 1;

      // Format: PI-YYYYMM-0001
      const poNumber = `PI-${yearMonth}-${String(nextNumber).padStart(4, '0')}`;
      setPurchaseOrder(prev => ({ ...prev, po_number: poNumber }));
    } catch (err) {
      console.error('Error generating PI number:', err);
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      setPurchaseOrder(prev => ({ ...prev, po_number: `PI-${year}${month}-${String(Date.now()).slice(-4)}` }));
    }
  };

  // Fetch company
  const fetchCompany = async () => {
    if (!companyId) return;
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      setCompany(data);
    } catch (err) {
      console.error('Error fetching company:', err);
    }
  };

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) throw error;
      setCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };


  // Fetch products
  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          brand:brand_id(id, name),
          variants:product_variants(
            id, name, sku, barcode, price, unit_price, cost_price,
            volume_weight, volume_unit, attributes, is_active
          )
        `)
        .eq('company_id', companyId)
        .order('name');

      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  // Fetch product variants
  const fetchProductVariants = async (productId) => {
    try {
      const { data: variants, error } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', productId)
        .eq('is_active', true);

      if (error) throw error;

      const { data: product, error: prodErr } = await supabase
        .from('products')
        .select('variants, has_variants')
        .eq('id', productId)
        .single();

      if (prodErr) throw prodErr;

      let allVariants = variants || [];

      if (product?.has_variants && product?.variants) {
        try {
          const jsonbVariants = Array.isArray(product.variants) ? product.variants : JSON.parse(product.variants);
          const formattedJsonbVariants = jsonbVariants.map(v => ({
            id: v.id || `jsonb-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            product_id: productId,
            name: v.name || 'Unnamed Variant',
            sku: v.sku || '',
            price: v.price || 0,
            unit_price: v.unit_price || 0,
            is_jsonb_variant: true
          }));
          allVariants = [...allVariants, ...formattedJsonbVariants];
        } catch (e) {
          console.error('Error parsing JSONB variants:', e);
        }
      }

      setProductVariants(prev => ({ ...prev, [productId]: allVariants }));
      return allVariants;
    } catch (err) {
      console.error('Error fetching variants:', err);
      return [];
    }
  };

  // Fetch price lists
  const fetchPriceLists = async () => {
    try {
      const { data, error } = await supabase
        .from('price_lists')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setPriceLists(data || []);
    } catch (err) {
      console.error('Error fetching price lists:', err);
    }
  };

  // Fetch locations
  const fetchLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('is_main', { ascending: false })
        .order('name');

      if (error) throw error;
      
      // Sort to put main location first
      const sortedData = (data || []).sort((a, b) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setInventoryLocations(sortedData);
      
      // Set default to main location if available, otherwise first location
      if (sortedData && sortedData.length > 0 && !selectedLocation) {
        const mainLocation = sortedData.find(loc => loc.is_main);
        setSelectedLocation(mainLocation ? mainLocation.id : sortedData[0].id);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  // Fetch inventory items
  const fetchInventoryItems = async (productId, variantId, locationId) => {
    if (!productId || !locationId) return [];
    
    try {
      const { data, error } = await supabase
        .from('product_inventory_items')
        .select('*')
        .eq('company_id', companyId)
        .eq('product_id', productId)
        .eq('location_id', locationId)
        .eq('is_active', true)
        .gt('total_base_units', 0);

      if (error) throw error;

      const filtered = (data || []).filter(item => {
        if (variantId) {
          return item.variant_id === variantId;
        } else {
          return !item.variant_id;
        }
      });

      return filtered;
    } catch (err) {
      console.error('Error fetching inventory:', err);
      return [];
    }
  };

  // Auto-fill billing info from customer record when customer is selected
  const fetchAndFillCustomerBilling = async (customerId) => {
    try {
      const { data: customer, error } = await supabase
        .from('customers')
        .select('street, city, state, zip_code, country, payment_terms')
        .eq('id', customerId)
        .single();

      if (error || !customer) return;

      setPurchaseOrder(prev => {
        const billingEmpty = !prev.billing_street && !prev.billing_city;
        return {
          ...prev,
          ...(billingEmpty ? {
            billing_street: customer.street || '',
            billing_city: customer.city || '',
            billing_state: customer.state || '',
            billing_zip: customer.zip_code || '',
            billing_country: customer.country || '',
          } : {}),
          payment_terms: customer.payment_terms || prev.payment_terms,
        };
      });
    } catch (err) {
      console.error('Error fetching customer billing:', err);
    }
  };

  // Update line item prices based on price list
  const updateLineItemPricesForPriceList = async (priceListId) => {
    if (lineItems.length === 0) return;

    try {
      const updatedItems = [...lineItems];
      let anyPriceChanged = false;

      if (!priceListId) {
        // STANDARD RETAIL PRICES - Get fresh product data
        const productIds = [...new Set(updatedItems
          .filter(item => item.product_id)
          .map(item => item.product_id))];

        if (productIds.length === 0) return;

        const { data: freshProducts, error: prodError } = await supabase
          .from('products')
          .select(`
            *,
            variants:product_variants(id, price, unit_price)
          `)
          .in('id', productIds);

        if (prodError) {
          console.error("Error fetching fresh product data:", prodError);
          return;
        }

        // Apply standard retail prices to all line items
        for (let i = 0; i < updatedItems.length; i++) {
          const item = updatedItems[i];
          if (!item.product_id) continue;

          const product = freshProducts?.find(p => p.id === item.product_id);
          if (!product) continue;

          let newPrice = parseFloat(product.default_price) || 0;

          // If this is a variant, get the variant price based on unit type
          if (item.variant_id) {
            const variant = product.variants?.find(v => v.id === item.variant_id);
            
            if (variant) {
              if (item.unit_type === 'Case') {
                newPrice = parseFloat(variant.price) || parseFloat(product.default_price) || 0;
              } else if (item.unit_type === 'Unit') {
                newPrice = parseFloat(variant.unit_price) || parseFloat(product.default_price) || 0;
              } else {
                // Default to case price for pallet when no price list
                newPrice = parseFloat(variant.price) || parseFloat(product.default_price) || 0;
              }
            }
          }

          if (newPrice > 0 && parseFloat(item.unit_price) !== parseFloat(newPrice)) {
            updatedItems[i] = {
              ...item,
              unit_price: newPrice,
              line_total: Number((item.quantity * newPrice).toFixed(2))
            };
            anyPriceChanged = true;
          }
        }
      } else {
        // PRICE LIST SELECTED - Get all price list items at once
        const { data: allPriceItems, error: itemsError } = await supabase
          .from('price_list_items')
          .select('*')
          .eq('price_list_id', priceListId);

        if (itemsError) {
          console.error("Error fetching price list items:", itemsError);
          return;
        }

        // Also get fresh product data for unit prices
        const productIds = [...new Set(updatedItems
          .filter(item => item.product_id)
          .map(item => item.product_id))];

        const { data: freshProducts, error: prodError } = await supabase
          .from('products')
          .select(`
            *,
            variants:product_variants(id, price, unit_price)
          `)
          .in('id', productIds);

        if (prodError) {
          console.error("Error fetching fresh product data:", prodError);
        }

        // Create a lookup map for quick access
        const priceMap = {};
        (allPriceItems || []).forEach(item => {
          const productId = item.product_id;
          if (!priceMap[productId]) {
            priceMap[productId] = {};
          }

          // For variants
          if (item.variant_id) {
            priceMap[productId][item.variant_id] = {
              price: item.price,
              case_price: item.case_price,
              pallet_price: item.pallet_price
            };
          } else {
            // For base product prices
            priceMap[productId]['base'] = {
              price: item.price,
              case_price: item.case_price,
              pallet_price: item.pallet_price
            };
          }
        });

        // Process each line item using the price map
        for (let i = 0; i < updatedItems.length; i++) {
          const item = updatedItems[i];
          if (!item.product_id) continue;

          const productId = item.product_id;
          let newPrice = 0;

          const unitType = item.unit_type || 'Case';

          // For Unit type, ALWAYS use the product variant's unit_price
          if (unitType === 'Unit') {
            const product = freshProducts?.find(p => p.id === productId);
            if (product && item.variant_id) {
              const variant = product.variants?.find(v => v.id === item.variant_id);
              if (variant) {
                newPrice = parseFloat(variant.unit_price) || 0;
              }
            } else if (product) {
              newPrice = parseFloat(product.default_price) || 0;
            }
          } else {
            // For Case and Pallet, use price list
            if (priceMap[productId]) {
              let priceSource = null;

              if (item.variant_id && priceMap[productId][item.variant_id]) {
                priceSource = priceMap[productId][item.variant_id];
              } else if (priceMap[productId]['base']) {
                priceSource = priceMap[productId]['base'];
              }

              // Get price based on unit type
              if (priceSource) {
                if (unitType === 'Case' && priceSource.case_price) {
                  newPrice = priceSource.case_price;
                } else if (unitType === 'Pallet' && priceSource.pallet_price) {
                  newPrice = priceSource.pallet_price;
                } else if (priceSource.price) {
                  // Default to regular price
                  newPrice = priceSource.price;
                }
              }
            }
          }

          // Only update if price is non-zero and different from current
          if (newPrice > 0 && parseFloat(item.unit_price) !== parseFloat(newPrice)) {
            updatedItems[i] = {
              ...item,
              unit_price: newPrice,
              line_total: Number((item.quantity * newPrice).toFixed(2))
            };
            anyPriceChanged = true;
          }
        }
      }

      if (anyPriceChanged) {
        setLineItems(updatedItems);
        recalculateTotals(updatedItems, purchaseOrder);
      }
    } catch (error) {
      console.error("Error updating prices:", error);
      setError('Failed to update prices: ' + error.message);
    }
  };

  // Update proforma invoice order
  const handlePOUpdate = (updates) => {
    setPurchaseOrder(prev => {
      const updated = { ...prev, ...updates };
      
      // Recalculate totals if financial fields changed
      if (updates.discount_percent !== undefined || 
          updates.tax_rate !== undefined || 
          updates.shipping_fee !== undefined ||
          updates.handling_fee !== undefined ||
          updates.other_fees !== undefined) {
        recalculateTotals(lineItems, updated);
      }

      if (updates.customer_id && updates.customer_id !== prev.customer_id) {
        fetchAndFillCustomerBilling(updates.customer_id);
      }

      // Update line item prices when price list changes
      if (updates.price_list_id !== undefined && updates.price_list_id !== prev.price_list_id) {
        updateLineItemPricesForPriceList(updates.price_list_id);
      }

      return updated;
    });
  };

  // Handle line item change
  const handleLineItemChange = async (index, field, value) => {
    const updated = [...lineItems];
    
    // Allow empty values for quantity and price during typing
    // Only enforce minimums on blur (handled in the input's onBlur)
    updated[index] = { ...updated[index], [field]: value };

    // Recalculate line total if quantity or price changed
    // Only calculate if both have valid numeric values
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(updated[index].quantity);
      const price = parseFloat(updated[index].unit_price);
      
      // Only calculate if both are valid numbers
      if (!isNaN(qty) && !isNaN(price) && qty > 0 && price >= 0) {
        updated[index].line_total = Number((qty * price).toFixed(2));
      } else {
        // Set to 0 if invalid
        updated[index].line_total = 0;
      }
    }

    // Handle unit type change - update price based on unit type
    if (field === 'unit_type') {
      const item = updated[index];
      let newPrice = 0;

      // Get fresh product data for unit prices
      const { data: freshProduct, error: prodError } = await supabase
        .from('products')
        .select(`
          *,
          variants:product_variants(id, price, unit_price)
        `)
        .eq('id', item.product_id)
        .single();

      if (prodError) {
        console.error("Error fetching product data:", prodError);
      }

      // For Unit type, ALWAYS use variant's unit_price from products
      if (value === 'Unit') {
        if (freshProduct && item.variant_id) {
          const variant = freshProduct.variants?.find(v => v.id === item.variant_id);
          if (variant) {
            newPrice = parseFloat(variant.unit_price) || 0;
          }
        } else if (freshProduct) {
          newPrice = parseFloat(freshProduct.default_price) || 0;
        }
      } else if (purchaseOrder.price_list_id && item.product_id) {
        // For Case and Pallet with price list, use price list pricing
        const { data: priceListItems, error } = await supabase
          .from('price_list_items')
          .select('*')
          .eq('price_list_id', purchaseOrder.price_list_id)
          .eq('product_id', item.product_id);

        if (!error && priceListItems && priceListItems.length > 0) {
          // Find the right price item for this variant
          const priceItem = item.variant_id 
            ? priceListItems.find(pi => pi.variant_id === item.variant_id)
            : priceListItems.find(pi => !pi.variant_id);

          if (priceItem) {
            if (value === 'Case' && priceItem.case_price) {
              newPrice = priceItem.case_price;
            } else if (value === 'Pallet' && priceItem.pallet_price) {
              newPrice = priceItem.pallet_price;
            } else if (priceItem.price) {
              newPrice = priceItem.price;
            }
          }
        }
      } else if (item.product_id) {
        // No price list - use standard retail prices
        if (freshProduct && item.variant_id) {
          const variant = freshProduct.variants?.find(v => v.id === item.variant_id);
          if (variant) {
            newPrice = parseFloat(variant.price) || 0;
          }
        } else if (freshProduct) {
          newPrice = parseFloat(freshProduct.default_price) || 0;
        }
      }

      if (newPrice > 0) {
        updated[index].unit_price = newPrice;
        updated[index].line_total = Number((updated[index].quantity * newPrice).toFixed(2));
      }
    }

    setLineItems(updated);
    recalculateTotals(updated, purchaseOrder);
  };

  // Add line item
  const handleAddLineItem = async () => {
    // Get main location ID (or fallback to selectedLocation or first location)
    const mainLocation = inventoryLocations.find(loc => loc.is_main);
    const defaultLocationId = mainLocation?.id || selectedLocation || (inventoryLocations.length > 0 ? inventoryLocations[0].id : null);
    
    setLineItems(prev => [
      ...prev,
      {
        id: `new-item-${prev.length + 1}`,
        product_id: '',
        variant_id: null,
        product_name: '',
        description: '',
        quantity: 1,
        unit_type: 'Case',
        unit_price: 0,
        line_total: 0,
        location_id: defaultLocationId,
        line_status: 'active'
      }
    ]);

    // Auto-save after adding line item
    setTimeout(() => {
      autoSave();
    }, 500);
  };

  // Remove line item
  const handleRemoveLineItem = async (index) => {
    const itemToRemove = lineItems[index];
    
    // FIRST: Update state immediately to remove from UI
    const updated = [...lineItems];
    updated.splice(index, 1);
    
    // Update state immediately
    setLineItems(updated);
    recalculateTotals(updated, purchaseOrder);
    
    // THEN: Unreserve inventory if this line item has an inventory_item_id
    if (itemToRemove?.inventory_item_id && companyId) {
      try {
        // Get all active reservations for this inventory item
        const { data: reservations, error: fetchError } = await supabase
          .from('product_inventory_reservations')
          .select('*')
          .eq('company_id', companyId)
          .eq('inventory_item_id', itemToRemove.inventory_item_id)
          .eq('is_active', true);
        
        if (!fetchError && reservations && reservations.length > 0) {
          // Cancel all reservations for this inventory item
          for (const reservation of reservations) {
            const { error: cancelError } = await supabase.rpc('cancel_inventory_reservation', {
              p_company_id: companyId,
              p_reservation_id: reservation.id
            });
            
            if (cancelError) {
              console.error('Error cancelling reservation:', cancelError);
            }
          }
        }
      } catch (err) {
        console.error('Error unreserving inventory when deleting line item:', err);
      }
    }

    // Auto-save after removing line item
    setTimeout(() => {
      autoSave();
    }, 500);
  };

  // Recalculate totals
  const recalculateTotals = useCallback((items, po) => {
    const totals = calculatePOTotals(
      items,
      po.discount_percent,
      po.tax_rate,
      po.shipping_fee,
      po.handling_fee,
      po.other_fees,
      undefined,
      po.discount_type || 'percent'
    );

    setPurchaseOrder(prev => ({ ...prev, ...totals }));
  }, []);

  // Keep ref in sync with state
  useEffect(() => {
    lineItemsRef.current = lineItems;
  }, [lineItems]);

  useEffect(() => {
    recalculateTotals(lineItems, purchaseOrder);
  }, [lineItems, purchaseOrder.discount_percent, purchaseOrder.discount_type, purchaseOrder.tax_rate, purchaseOrder.shipping_fee, purchaseOrder.handling_fee, purchaseOrder.other_fees]);

  // Auto-save PI (silent save without messages or navigation)
  const autoSave = async () => {
    // Only auto-save if we have required fields and we're in edit mode or have a saved PI
    if (!purchaseOrder.po_number || !purchaseOrder.customer_id) {
      return; // Don't auto-save if missing required fields
    }

    // Don't auto-save if we're already saving
    if (saving) return;

    try {
      // Use purchaseOrder.id if it exists (from fetchPO), otherwise use poId from URL
      let poCreationId = purchaseOrder.id || poId;
      let customerPurchaseOrderId = propPurchaseOrder?.id;

      // Sanitize UUID fields - convert empty strings to null
      const sanitizeUUIDs = (data) => {
        const uuidFields = ['price_list_id', 'customer_id', 'purchase_order_id', 'approved_by', 'rejected_by', 'sent_by', 'last_printed_by', 'cancelled_by', 'last_modified_by', 'completed_by'];
        const sanitized = { ...data };
        uuidFields.forEach(field => {
          if (sanitized[field] === '' || sanitized[field] === undefined) {
            sanitized[field] = null;
          }
        });
        return sanitized;
      };

      // STEP 1: Ensure we have a customer_purchase_orders record
      if (!customerPurchaseOrderId) {
        // Create a customer_purchase_orders record first
        const { data: newCPO, error: cpoError } = await supabase
          .from('customer_purchase_orders')
          .insert({
            company_id: companyId,
            po_number: purchaseOrder.po_number,
            status: purchaseOrder.po_status || 'draft',
            current_stage: 'po_creation',
            stage_completed: ['po_creation'],
            customer_id: purchaseOrder.customer_id,
            customer_name: purchaseOrder.customer_name,
            customer_company: purchaseOrder.customer_company,
            customer_po_number: purchaseOrder.customer_po_number,
            order_date: purchaseOrder.po_date,
            requested_delivery_date: purchaseOrder.requested_delivery_date || null,
            price_list_id: purchaseOrder.price_list_id || null,
            payment_terms: purchaseOrder.payment_terms,
            payment_method: purchaseOrder.payment_method || null,
            customer_notes: purchaseOrder.customer_notes || null
          })
          .select()
          .single();

        if (cpoError) {
          console.error('Auto-save error (CPO):', cpoError);
          return;
        }
        customerPurchaseOrderId = newCPO.id;
      } else {
        // Update existing customer_purchase_orders
        const { error: updateCPOErr } = await supabase
          .from('customer_purchase_orders')
          .update({
            po_number: purchaseOrder.po_number,
            status: purchaseOrder.po_status || 'draft',
            customer_id: purchaseOrder.customer_id,
            customer_name: purchaseOrder.customer_name,
            customer_company: purchaseOrder.customer_company,
            customer_po_number: purchaseOrder.customer_po_number,
            order_date: purchaseOrder.po_date,
            requested_delivery_date: purchaseOrder.requested_delivery_date || null,
            price_list_id: purchaseOrder.price_list_id || null,
            payment_terms: purchaseOrder.payment_terms,
            payment_method: purchaseOrder.payment_method || null,
            customer_notes: purchaseOrder.customer_notes || null,
            current_stage: 'po_creation',
            stage_completed: propPurchaseOrder?.stage_completed 
              ? [...new Set([...propPurchaseOrder.stage_completed, 'po_creation'])]
              : ['po_creation']
          })
          .eq('id', customerPurchaseOrderId);

        if (updateCPOErr) {
          console.error('Auto-save error (update CPO):', updateCPOErr);
          return;
        }
      }

      // STEP 2: Create or update po_creation record
      if (isEdit && poCreationId) {
        // Update existing
        const { error: updateErr } = await supabase
          .from('po_creation')
          .update(sanitizeUUIDs({
            ...purchaseOrder,
            purchase_order_id: customerPurchaseOrderId,
            updated_at: new Date().toISOString()
          }))
          .eq('id', poCreationId);

        if (updateErr) {
          console.error('Auto-save error (update):', updateErr);
          return;
        }

        // Delete old items
        await supabase.from('po_creation_items').delete().eq('po_creation_id', poCreationId);
      } else {
        // Create new
        const { data, error: createErr } = await supabase
          .from('po_creation')
          .insert(sanitizeUUIDs({
            company_id: companyId,
            purchase_order_id: customerPurchaseOrderId,
            ...purchaseOrder
          }))
          .select()
          .single();

        if (createErr) {
          console.error('Auto-save error (create):', createErr);
          return;
        }
        poCreationId = data.id;
        setPurchaseOrder(prev => ({ ...prev, id: data.id }));
        setIsEdit(true);
      }

      // STEP 3: Insert line items (use ref to get latest state)
      const currentLineItems = lineItemsRef.current;
      const itemsToInsert = currentLineItems
        .filter(li => li.description && li.description.trim() !== '')
        .map((li, idx) => ({
          po_creation_id: poCreationId,
          company_id: companyId,
          purchase_order_id: customerPurchaseOrderId,
          product_selection_id: li.product_selection_id || null,
          product_id: (li.product_id && li.product_id !== '') ? li.product_id : null,
          variant_id: (li.variant_id && li.variant_id !== '') ? li.variant_id : null,
          variant_name: li.variant_name || '',
          product_name: li.product_name || '',
          product_sku: li.product_sku || '',
          description: li.description.trim(),
          quantity: parseFloat(li.quantity) || 0,
          unit_type: li.unit_type || 'Case',
          units_per_case: li.units_per_case || 1,
          cases_per_pallet: li.cases_per_pallet || 1,
          location_id: (li.location_id && li.location_id !== '') ? li.location_id : null,
          location_name: li.location_name || '',
          inventory_item_id: (li.inventory_item_id && li.inventory_item_id !== '') ? li.inventory_item_id : null,
          lot_number: li.lot_number || null,
          batch_number: li.batch_number || null,
          manufactured_date: li.manufactured_date || null,
          expiry_date: li.expiry_date || null,
          shelf_life_remaining: li.shelf_life_remaining || null,
          unit_price: parseFloat(li.unit_price) || 0,
          discount_percent: parseFloat(li.discount_percent) || 0,
          discount_amount: parseFloat(li.discount_amount) || 0,
          line_subtotal: parseFloat(li.line_subtotal) || 0,
          line_total: parseFloat(li.line_total) || 0,
          line_status: li.line_status || 'active',
          line_notes: li.line_notes || '',
          line_order: idx
        }));

      if (itemsToInsert.length > 0) {
        const { error: itemsErr } = await supabase.from('po_creation_items').insert(itemsToInsert);
        if (itemsErr) {
          console.error('Auto-save error (items):', itemsErr);
          return;
        }
      }

      // Call parent update if in workflow mode
      if (propOnUpdate && customerPurchaseOrderId) {
        propOnUpdate(customerPurchaseOrderId);
      }
    } catch (err) {
      console.error('Error auto-saving PI:', err);
      // Don't show error to user for auto-save
    }
  };

  // Save PI
  const handleSave = async () => {
    if (!purchaseOrder.po_number) {
      setError('PI number is required');
      return;
    }
    if (!purchaseOrder.customer_id) {
      setError('Please select a customer');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Use purchaseOrder.id if it exists (from fetchPO), otherwise use poId from URL
      let poCreationId = purchaseOrder.id || poId;
      let customerPurchaseOrderId = propPurchaseOrder?.id;

      // Sanitize UUID fields - convert empty strings to null
      const sanitizeUUIDs = (data) => {
        const uuidFields = ['price_list_id', 'customer_id', 'purchase_order_id', 'approved_by', 'rejected_by', 'sent_by', 'last_printed_by', 'cancelled_by', 'last_modified_by', 'completed_by'];
        const sanitized = { ...data };
        uuidFields.forEach(field => {
          if (sanitized[field] === '' || sanitized[field] === undefined) {
            sanitized[field] = null;
          }
        });
        return sanitized;
      };

      // STEP 1: Ensure we have a customer_purchase_orders record
      if (!customerPurchaseOrderId) {
        // Create a customer_purchase_orders record first
        const { data: newCPO, error: cpoError } = await supabase
          .from('customer_purchase_orders')
          .insert({
            company_id: companyId,
            po_number: purchaseOrder.po_number,
            status: purchaseOrder.po_status || 'draft',
            current_stage: 'po_creation',
            stage_completed: ['po_creation'],
            customer_id: purchaseOrder.customer_id,
            customer_name: purchaseOrder.customer_name,
            customer_company: purchaseOrder.customer_company,
            customer_po_number: purchaseOrder.customer_po_number,
            order_date: purchaseOrder.po_date,
            requested_delivery_date: purchaseOrder.requested_delivery_date || null,
            price_list_id: purchaseOrder.price_list_id || null,
            payment_terms: purchaseOrder.payment_terms,
            payment_method: purchaseOrder.payment_method || null,
            customer_notes: purchaseOrder.customer_notes || null
          })
          .select()
          .single();

        if (cpoError) throw cpoError;
        customerPurchaseOrderId = newCPO.id;
      } else {
        // Update existing customer_purchase_orders
        const { error: updateCPOErr } = await supabase
          .from('customer_purchase_orders')
          .update({
            po_number: purchaseOrder.po_number,
            status: purchaseOrder.po_status || 'draft',
            customer_id: purchaseOrder.customer_id,
            customer_name: purchaseOrder.customer_name,
            customer_company: purchaseOrder.customer_company,
            customer_po_number: purchaseOrder.customer_po_number,
            order_date: purchaseOrder.po_date,
            requested_delivery_date: purchaseOrder.requested_delivery_date || null,
            price_list_id: purchaseOrder.price_list_id || null,
            payment_terms: purchaseOrder.payment_terms,
            payment_method: purchaseOrder.payment_method || null,
            customer_notes: purchaseOrder.customer_notes || null,
            current_stage: 'po_creation',
            stage_completed: propPurchaseOrder?.stage_completed 
              ? [...new Set([...propPurchaseOrder.stage_completed, 'po_creation'])]
              : ['po_creation']
          })
          .eq('id', customerPurchaseOrderId);

        if (updateCPOErr) throw updateCPOErr;
      }

      // STEP 2: Create or update po_creation record
      if (isEdit && poCreationId) {
        // Update existing
        const { error: updateErr } = await supabase
          .from('po_creation')
          .update(sanitizeUUIDs({
            ...purchaseOrder,
            purchase_order_id: customerPurchaseOrderId,
            updated_at: new Date().toISOString()
          }))
          .eq('id', poCreationId);

        if (updateErr) throw updateErr;

        // Delete old items
        await supabase.from('po_creation_items').delete().eq('po_creation_id', poCreationId);
      } else {
        // Create new
        const { data, error: createErr } = await supabase
          .from('po_creation')
          .insert(sanitizeUUIDs({
            company_id: companyId,
            purchase_order_id: customerPurchaseOrderId,
            ...purchaseOrder
          }))
          .select()
          .single();

        if (createErr) throw createErr;
        poCreationId = data.id;
      }

      // STEP 3: Insert line items
      const itemsToInsert = lineItems
        .filter(li => li.description && li.description.trim() !== '')
        .map((li, idx) => ({
          po_creation_id: poCreationId,
          company_id: companyId,
          purchase_order_id: customerPurchaseOrderId,
          product_selection_id: li.product_selection_id || null,
          product_id: (li.product_id && li.product_id !== '') ? li.product_id : null,
          variant_id: (li.variant_id && li.variant_id !== '') ? li.variant_id : null,
          variant_name: li.variant_name || '',
          product_name: li.product_name || '',
          product_sku: li.product_sku || '',
          description: li.description.trim(),
          quantity: parseFloat(li.quantity) || 0,
          unit_type: li.unit_type || 'Case',
          units_per_case: li.units_per_case || 1,
          cases_per_pallet: li.cases_per_pallet || 1,
          location_id: (li.location_id && li.location_id !== '') ? li.location_id : null,
          location_name: li.location_name || '',
          inventory_item_id: (li.inventory_item_id && li.inventory_item_id !== '') ? li.inventory_item_id : null,
          lot_number: li.lot_number || null,
          batch_number: li.batch_number || null,
          manufactured_date: li.manufactured_date || null,
          expiry_date: li.expiry_date || null,
          shelf_life_remaining: li.shelf_life_remaining || null,
          unit_price: parseFloat(li.unit_price) || 0,
          discount_percent: parseFloat(li.discount_percent) || 0,
          discount_amount: parseFloat(li.discount_amount) || 0,
          line_subtotal: parseFloat(li.line_subtotal) || 0,
          line_total: parseFloat(li.line_total) || 0,
          line_status: li.line_status || 'active',
          line_notes: li.line_notes || '',
          line_order: idx
        }));

      if (itemsToInsert.length > 0) {
        const { error: itemsErr } = await supabase.from('po_creation_items').insert(itemsToInsert);
        if (itemsErr) throw itemsErr;
      }

      // Set success message
      setSuccessMessage('Proforma Invoice saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setSaving(false);

      // Call parent update if in workflow mode
      if (propOnUpdate) {
        // Pass the customer_purchase_orders ID (which we ensured exists above)
        propOnUpdate(customerPurchaseOrderId);
      } else {
        // Only navigate if standalone mode (not in workflow)
        setTimeout(() => {
          navigate(`/company/${companyId}/proforma-invoices`);
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving PI:', err);
      setError('Failed to save proforma invoice: ' + err.message);
      setSaving(false);
    }
  };

  // Format date without timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Print handler
  const handlePrint = () => {
    document.title = `ProformaInvoice-${purchaseOrder.po_number}`;
    
    try {
      const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount || 0);
      };

      // Convert country code to full name
      const getCountryFullName = (country) => {
        if (!country) return '';
        const countryMap = {
          'US': 'United States',
          'USA': 'United States',
          'United States': 'United States',
          'CA': 'Canada',
          'Canada': 'Canada',
          'MX': 'Mexico',
          'Mexico': 'Mexico',
          'UK': 'United Kingdom',
          'GB': 'United Kingdom',
          'United Kingdom': 'United Kingdom'
        };
        return countryMap[country] || country;
      };

      const formatAddressFromFields = (street, city, state, zip, country) => {
        const addressParts = [];
        if (street) addressParts.push(street);
        
        const cityStateZip = [];
        if (city) cityStateZip.push(city);
        if (state) cityStateZip.push(state);
        if (zip) cityStateZip.push(zip);
        
        if (cityStateZip.length > 0) {
          addressParts.push(cityStateZip.join(', '));
        }
        
        if (country) addressParts.push(getCountryFullName(country));
        
        return addressParts.join('<br/>');
      };

      const formatCompanyAddr = () => {
        if (!company) return '';
        
        let address = '';
        if (company.address) address += company.address;
        
        if (company.city || company.state || company.zip_code) {
          address += address ? '<br/>' : '';
          if (company.city) address += company.city;
          if (company.state) {
            if (company.city) address += ', ';
            address += company.state;
          }
          if (company.zip_code) {
            if (company.city || company.state) address += ' ';
            address += company.zip_code;
          }
        }
        
        if (company.country) {
          address += address ? '<br/>' : '';
          address += getCountryFullName(company.country);
        }
        
        return address;
      };

      const printableContent = `
        <div style="padding: 16px; backgroundColor: #fff; font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 20px;">
            <div>
              <h1 style="margin: 0 0 6px 0; font-size: 1.75rem; font-weight: 400;">PROFORMA INVOICE</h1>
              <p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                PI #: ${purchaseOrder.po_number}
              </p>
              <p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                Date: ${formatDate(purchaseOrder.po_date)}
              </p>
              ${purchaseOrder.requested_delivery_date ? `<p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                Requested Delivery: ${formatDate(purchaseOrder.requested_delivery_date)}
              </p>` : ''}
            </div>
            <div style="text-align: right;">
              <h2 style="margin: 0 0 6px 0; font-size: 1.1rem; font-weight: 500;">
                ${company?.name || 'Your Company Name'}
              </h2>
              <p style="margin: 1px 0; font-size: 0.8rem;">
                ${formatCompanyAddr() || 'Address not provided'}
              </p>
              ${company?.phone ? `<p style="margin: 1px 0; font-size: 0.8rem;">
                Phone: ${company.phone}
              </p>` : ''}
              ${company?.email ? `<p style="margin: 1px 0; font-size: 0.8rem;">
                Email: ${company.email}
              </p>` : ''}
              ${company?.website ? `<p style="margin: 1px 0; font-size: 0.8rem;">
                Web: ${company.website}
              </p>` : ''}
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
            <div>
              <h3 style="margin: 0 0 6px 0; font-weight: 600; font-size: 0.9rem;">Bill To:</h3>
              <p style="margin: 2px 0; font-size: 0.8rem;">${getCustomerName()}</p>
              ${selectedCustomer?.company ? `<p style="margin: 2px 0; font-size: 0.8rem;">
                Company: ${selectedCustomer.company}
              </p>` : ''}
              ${selectedCustomer?.phone ? `<p style="margin: 2px 0; font-size: 0.8rem;">
                Phone: ${selectedCustomer.phone}
              </p>` : ''}
              ${selectedCustomer?.email ? `<p style="margin: 2px 0; font-size: 0.8rem;">
                Email: ${selectedCustomer.email}
              </p>` : ''}
              <p style="margin: 6px 0; font-size: 0.8rem;"></p>
              <p style="margin: 2px 0; font-size: 0.8rem;">
                Address:<br/>${formatAddressFromFields(
                  purchaseOrder.billing_street,
                  purchaseOrder.billing_city,
                  purchaseOrder.billing_state,
                  purchaseOrder.billing_zip,
                  purchaseOrder.billing_country
                ) || 'No billing address provided'}
              </p>
            </div>
            ${(purchaseOrder.shipping_street || purchaseOrder.shipping_city || purchaseOrder.shipping_state || purchaseOrder.shipping_zip || purchaseOrder.shipping_country) ? `
            <div>
              <h3 style="margin: 0 0 6px 0; font-weight: 600; font-size: 0.9rem;">Ship To:</h3>
              <p style="margin: 2px 0; font-size: 0.8rem;">${getCustomerName()}</p>
              <p style="margin: 6px 0; font-size: 0.8rem;"></p>
              <p style="margin: 2px 0; font-size: 0.8rem;">
                Address:<br/>${formatAddressFromFields(
                  purchaseOrder.shipping_street,
                  purchaseOrder.shipping_city,
                  purchaseOrder.shipping_state,
                  purchaseOrder.shipping_zip,
                  purchaseOrder.shipping_country
                )}
              </p>
            </div>` : ''}
          </div>

          ${(printOption === 'summary' || printOption === 'both') ? `
          <!-- Order Summary Table -->
          <div style="margin: 24px 0 12px 0;">
            <h3 style="margin: 0 0 8px 0; padding-bottom: 8px; font-weight: 600; font-size: 1.1rem; border-bottom: 1px solid rgba(0, 0, 0, 0.12);">Order Summary</h3>
          </div>
          <div style="border: 1px solid rgba(224, 224, 224, 1); border-radius: 4px; margin: 16px 0; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: rgb(245, 245, 245);">
                  <th style="padding: 10px; text-align: left; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Product</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Size</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">UoM</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Quantity</th>
                  <th style="padding: 10px; text-align: right; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Unit Price</th>
                  <th style="padding: 10px; text-align: right; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${(() => {
                  const summaryMap = new Map();

                  lineItems.filter(item => item.description).forEach(item => {
                    const key = `${item.product_id || 'no-product'}_${item.variant_id || 'no-variant'}_${item.unit_type || 'Case'}`;

                    if (!summaryMap.has(key)) {
                      summaryMap.set(key, {
                        product_name: item.product_name || item.description?.split(' - ')[0] || 'N/A',
                        variant_name: item.variant_name || null,
                        unit_type: item.unit_type || 'Case',
                        units_per_case: item.units_per_case || null,
                        unit_price: parseFloat(item.unit_price) || 0,
                        quantity: 0,
                        total_amount: 0
                      });
                    }

                    const summary = summaryMap.get(key);
                    summary.quantity += parseFloat(item.quantity) || 0;
                    summary.total_amount += parseFloat(item.line_total || item.total_price) || 0;
                  });

                  return Array.from(summaryMap.values()).map((summary) => {
                    const isCase = (summary.unit_type || 'Case') === 'Case';
                    const qtyLabel = isCase
                      ? `${summary.quantity} ${summary.quantity === 1 ? 'Case' : 'Cases'}`
                      : `${summary.quantity}`;

                    return `
                    <tr>
                      <td style="padding: 8px 10px; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${summary.product_name}</td>
                      <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${summary.variant_name || '-'}</td>
                      <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${summary.units_per_case ? `${summary.units_per_case}U/C` : '-'}</td>
                      <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${qtyLabel}</td>
                      <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${formatCurrency(summary.unit_price)}</td>
                      <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${formatCurrency(summary.total_amount)}</td>
                    </tr>
                    `;
                  }).join('');
                })()}
              </tbody>
            </table>
          </div>
          ` : ''}

          ${(printOption === 'lotBreakdown' || printOption === 'both') ? `
          <!-- Lot Breakdown Table -->
          <div style="margin: 24px 0 12px 0;">
            <h3 style="margin: 0 0 8px 0; padding-bottom: 8px; font-weight: 600; font-size: 1.1rem; border-bottom: 1px solid rgba(0, 0, 0, 0.12);">Lot Breakdown</h3>
          </div>
          <div style="border: 1px solid rgba(224, 224, 224, 1); border-radius: 4px; margin: 16px 0; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: rgb(245, 245, 245);">
                  <th style="padding: 10px; text-align: left; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Product</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Size</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">UoM</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Qty</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Lot Number</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Mfg Date</th>
                  <th style="padding: 10px; text-align: center; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Exp Date</th>
                  <th style="padding: 10px; text-align: right; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Unit Price</th>
                  <th style="padding: 10px; text-align: right; font-weight: 600; font-size: 0.8rem; border-bottom: 1px solid rgba(224, 224, 224, 1);">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${lineItems.filter(item => item.description).map(item => {
                  const isCase = (item.unit_type || 'Case') === 'Case';
                  const qty = parseFloat(item.quantity) || 0;
                  const qtyLabel = isCase ? `${qty} ${qty === 1 ? 'Case' : 'Cases'}` : `${qty}`;

                  return `
                  <tr>
                    <td style="padding: 8px 10px; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${item.product_name || item.description?.split(' - ')[0] || 'N/A'}</td>
                    <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${item.variant_name || '-'}</td>
                    <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${item.units_per_case ? `${item.units_per_case}U/C` : '-'}</td>
                    <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${qtyLabel}</td>
                    <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${item.lot_number || '-'}</td>
                    <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${item.manufactured_date ? formatDate(item.manufactured_date) : '-'}</td>
                    <td style="padding: 8px 10px; text-align: center; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${item.expiry_date ? formatDate(item.expiry_date) : '-'}</td>
                    <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${formatCurrency(item.unit_price)}</td>
                    <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid rgba(224, 224, 224, 1); font-size: 0.8rem;">${formatCurrency(item.line_total || item.total_price)}</td>
                  </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}

          <div style="display: flex; justify-content: flex-end; margin: 16px 0;">
            <div style="width: 250px;">
              <div style="display: flex; justify-content: space-between; margin: 2px 0;">
                <span style="font-size: 0.85rem;">Subtotal:</span>
                <span style="font-size: 0.85rem;">${formatCurrency(purchaseOrder.subtotal)}</span>
              </div>

              ${parseFloat(purchaseOrder.discount_percent) > 0 ? `
              <div style="display: flex; justify-content: space-between; margin: 2px 0;">
                <span style="font-size: 0.85rem;">${purchaseOrder.discount_type === 'flat' ? 'Discount:' : `Discount (${purchaseOrder.discount_percent}%):`}</span>
                <span style="font-size: 0.85rem;">-${formatCurrency(purchaseOrder.discount_amount)}</span>
              </div>` : ''}

              ${parseFloat(purchaseOrder.tax_amount) > 0 ? `
              <div style="display: flex; justify-content: space-between; margin: 2px 0;">
                <span style="font-size: 0.85rem;">Tax (${purchaseOrder.tax_rate}%):</span>
                <span style="font-size: 0.85rem;">+${formatCurrency(purchaseOrder.tax_amount)}</span>
              </div>` : ''}

              ${parseFloat(purchaseOrder.shipping_fee) > 0 ? `
              <div style="display: flex; justify-content: space-between; margin: 2px 0;">
                <span style="font-size: 0.85rem;">Shipping:</span>
                <span style="font-size: 0.85rem;">+${formatCurrency(purchaseOrder.shipping_fee)}</span>
              </div>` : ''}

              ${parseFloat(purchaseOrder.handling_fee) > 0 ? `
              <div style="display: flex; justify-content: space-between; margin: 2px 0;">
                <span style="font-size: 0.85rem;">Handling:</span>
                <span style="font-size: 0.85rem;">+${formatCurrency(purchaseOrder.handling_fee)}</span>
              </div>` : ''}

              <div style="border-top: 1px solid rgba(0, 0, 0, 0.12); padding-top: 10px; margin-top: 10px;">
                <div style="display: flex; justify-content: space-between; margin: 2px 0;">
                  <span style="font-size: 1rem; font-weight: 600;">Total:</span>
                  <span style="font-size: 1rem; font-weight: 600; color:rgb(0, 0, 0);">${formatCurrency(purchaseOrder.total_before_credit)}</span>
                </div>
              </div>

            </div>
          </div>

          ${(purchaseOrder.payment_terms || purchaseOrder.payment_method || parseFloat(purchaseOrder.late_fee_rate) > 0 || parseFloat(purchaseOrder.early_payment_discount) > 0) ? `
          <div style="margin: 16px 0;">
            <h3 style="margin: 0 0 6px 0; font-weight: 600; font-size: 0.9rem;">Payment Terms:</h3>
            ${purchaseOrder.payment_terms ? `<p style="margin: 2px 0; font-size: 0.8rem;">Payment Due: ${purchaseOrder.payment_terms}</p>` : ''}
            ${purchaseOrder.payment_method ? `<p style="margin: 2px 0; font-size: 0.8rem;">Preferred Method: ${purchaseOrder.payment_method}</p>` : ''}
            ${parseFloat(purchaseOrder.early_payment_discount) > 0 ? `<p style="margin: 2px 0; font-size: 0.8rem;">Early Payment: ${purchaseOrder.early_payment_discount}% discount if paid within ${purchaseOrder.early_payment_days || 10} days</p>` : ''}
            ${parseFloat(purchaseOrder.late_fee_rate) > 0 ? `<p style="margin: 2px 0; font-size: 0.8rem;">Late Fee: ${purchaseOrder.late_fee_rate}% per month on overdue balances</p>` : ''}
          </div>` : ''}

          ${purchaseOrder.customer_notes ? `
          <div style="margin: 16px 0;">
            <h3 style="margin: 0 0 6px 0; font-weight: 600; font-size: 0.9rem;">Notes:</h3>
            <p style="margin: 6px 0; white-space: pre-line; font-size: 0.8rem;">${purchaseOrder.customer_notes}</p>
          </div>` : ''}

          ${purchaseOrder.special_instructions ? `
          <div style="margin: 16px 0;">
            <h3 style="margin: 0 0 6px 0; font-weight: 600; font-size: 0.9rem;">Special Instructions:</h3>
            <p style="margin: 6px 0; white-space: pre-line; font-size: 0.8rem;">${purchaseOrder.special_instructions}</p>
          </div>` : ''}

          ${purchaseOrder.terms_and_conditions ? `
          <div class="terms-box" style="margin: 20px 0; padding: 16px; border: 1px solid #000;">
            <h3 style="margin: 0 0 10px 0; font-weight: 600; font-size: 0.9rem;">Terms & Conditions</h3>
            <p style="margin: 0; white-space: pre-line; font-size: 0.75rem; line-height: 1.4;">${purchaseOrder.terms_and_conditions}</p>
          </div>` : ''}

          <div style="margin-top: 20px; display: flex; justify-content: space-between; align-items: flex-end;">
            <div style="text-align: center;">
              <p style="font-size: 0.8rem; color: rgba(0, 0, 0, 0.6);">
                Thank you for your business!
              </p>
            </div>
            
            ${purchaseOrder.prepared_by && purchaseOrder.prepared_by.name ? `
            <div style="text-align: right; min-width: 200px; break-inside: avoid;">
              <p style="margin: 2px 0; font-size: 0.8rem; font-weight: 600;">
                Prepared by:
              </p>
              <div style="font-family: 'Brush Script MT', 'Brush Script Std', cursive; font-size: 16pt; color: #000000; margin: 6px 0; text-shadow: 1px 1px 1px rgba(0,0,0,0.1);">
                ${purchaseOrder.prepared_by.name}
              </div>
              <p style="margin: 1px 0; font-size: 0.8rem; color: rgba(0, 0, 0, 0.6);">
                ${purchaseOrder.prepared_by.position || 'Sales Representative'}
              </p>
              <p style="margin: 1px 0; font-size: 0.75rem; color: rgba(0, 0, 0, 0.6);">
                ${formatDate(purchaseOrder.prepared_by.date)}
              </p>
            </div>` : ''}
          </div>
        </div>
      `;

      // Create new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Proforma Invoice ${purchaseOrder.po_number}</title>
          <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap" rel="stylesheet">
          <style>
            @page { 
              size: auto; 
              margin: 0.3in; 
            }
            @media print {
              body { 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .terms-box {
                box-decoration-break: clone;
                -webkit-box-decoration-break: clone;
              }
            }
            body {
              margin: 0;
              font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
              line-height: 1.3;
              letter-spacing: 0.01071em;
              background: white;
            }
            .terms-box {
              box-decoration-break: clone;
              -webkit-box-decoration-break: clone;
            }
          </style>
        </head>
        <body>
          ${printableContent}
        </body>
        </html>
      `;

      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        
        // Close the print window after printing
        setTimeout(() => {
          printWindow.close();
          // Close the preview dialog after printing
          setPrintDialogOpen(false);
        }, 1000);
      };
      
    } catch (error) {
      console.error('Print error:', error);
      setError('Error printing proforma invoice. Please try again.');
    }
  };

  // Signature handling
  const handleSaveSignature = () => {
    if (!signaturePreview.name.trim()) {
      setError('Please enter a name');
      return;
    }
    
    handlePOUpdate({
      prepared_by: {
        name: signaturePreview.name,
        position: signaturePreview.position,
        date: new Date().toISOString(),
        typed: true
      }
    });
    
    setSignatureDialogOpen(false);
  };

  const getCustomerName = () => {
    if (purchaseOrder.customer_name) return purchaseOrder.customer_name;
    const c = customers.find(cust => cust.id === purchaseOrder.customer_id);
    return c?.name || 'Select a Customer';
  };

  const selectedCustomer = customers.find(c => c.id === purchaseOrder.customer_id) || null;

  // Access Denied if no view permission
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              PI Creation Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Proforma Invoice Creation. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          p: 4,
          textAlign: 'center'
        }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <LinearProgress sx={{ mb: 3 }} />
          <Typography variant="h6" color="primary.main">
            Loading Proforma Invoice Form...
          </Typography>
        </Card>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Box>
          <Container maxWidth={false} sx={{ py: 3, pb: 8, px: 0, marginLeft: '120px'}}>
            {/* Page Header */}
            <Card sx={{ 
              borderRadius: 3,
              mb: 3,
              mt: 3,
              width: 'auto',
              border: `1px solid ${theme.palette.grey[400]}`,
              background: theme.palette.grey[100],
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 3, 
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                    }}>
                      <AssignmentIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        fontSize: '2rem'
                      }}>
                        {isEdit ? `Edit PI #${purchaseOrder.po_number}` : 'New Proforma Invoice'}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" fontWeight={500}>
                        {company?.name || 'Loading...'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined" 
                      startIcon={<PrintIcon />} 
                      onClick={() => setPrintDialogOpen(true)}
                      sx={{ 
                        borderRadius: 3, 
                        px: 3, 
                        py: 1.5,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={canEdit ? handleSave : undefined}
                      disabled={saving}
                      sx={{ 
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        opacity: canEdit ? 1 : 0.3,
                        pointerEvents: canEdit ? 'auto' : 'none',
                        cursor: canEdit ? 'pointer' : 'not-allowed',
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
                        '&:hover': {
                          transform: canEdit ? 'translateY(-2px)' : 'none',
                          boxShadow: canEdit ? `0 4px 16px ${alpha(theme.palette.primary.main, 0.35)}` : `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Proforma Invoice'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3, 
                  borderRadius: 3, 
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  backgroundColor: alpha(theme.palette.success.main, 0.1)
                }}
                onClose={() => setSuccessMessage('')}
              >
                {successMessage}
              </Alert>
            )}

            {/* 1. PI Details */}
            <CollapsibleSection title="PI Details" expanded={!!accordion.piDetails} onToggle={() => toggleSection('piDetails')}>
              <PISummarySection
                purchaseOrder={purchaseOrder}
                onUpdate={handlePOUpdate}
                canEdit={canEdit}
                poId={poId}
                showDetails={true}
                showSummary={false}
                showPaymentTerms={false}
                showNotes={false}
              />
            </CollapsibleSection>

            {/* 2. Customer Information */}
            <CollapsibleSection title="Customer Information" expanded={!!accordion.customerInfo} onToggle={() => toggleSection('customerInfo')}>
              <PICustomerSection
                purchaseOrder={purchaseOrder}
                customers={customers}
                onUpdate={handlePOUpdate}
                companyId={companyId}
                canEdit={canEdit}
              />
            </CollapsibleSection>

            {/* 3. Line Items */}
            <CollapsibleSection title="Line Items" expanded={!!accordion.lineItems} onToggle={() => toggleSection('lineItems')}>
              <PILineItemsSection
                lineItems={lineItems}
                products={products}
                productVariants={productVariants}
                priceLists={priceLists}
                currentCompany={company}
                inventoryLocations={inventoryLocations}
                selectedLocation={selectedLocation}
                purchaseOrder={purchaseOrder}
                onLineItemsChange={setLineItems}
                onLineItemChange={handleLineItemChange}
                onAddLineItem={handleAddLineItem}
                onRemoveLineItem={handleRemoveLineItem}
                onFetchProductVariants={fetchProductVariants}
                onFetchInventoryItems={fetchInventoryItems}
                onSetSelectedLocation={setSelectedLocation}
                onUpdate={handlePOUpdate}
                companyId={companyId}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            </CollapsibleSection>

            {/* 4. Payment Terms */}
            <CollapsibleSection title="Payment Terms" expanded={!!accordion.paymentTerms} onToggle={() => toggleSection('paymentTerms')}>
              <PISummarySection
                purchaseOrder={purchaseOrder}
                onUpdate={handlePOUpdate}
                canEdit={canEdit}
                poId={poId}
                showDetails={false}
                showSummary={false}
                showPaymentTerms={true}
                showNotes={false}
              />
            </CollapsibleSection>

            {/* 5. Summary */}
            <CollapsibleSection title="Summary" expanded={!!accordion.summary} onToggle={() => toggleSection('summary')}>
              <PISummarySection
                purchaseOrder={purchaseOrder}
                onUpdate={handlePOUpdate}
                canEdit={canEdit}
                poId={poId}
                showDetails={false}
                showSummary={true}
                showPaymentTerms={false}
                showNotes={false}
              />
            </CollapsibleSection>

            {/* 7. Additional Notes */}
            <CollapsibleSection title="Additional Notes" expanded={!!accordion.additionalNotes} onToggle={() => toggleSection('additionalNotes')}>
              <PISummarySection
                purchaseOrder={purchaseOrder}
                onUpdate={handlePOUpdate}
                canEdit={canEdit}
                poId={poId}
                showDetails={false}
                showSummary={false}
                showPaymentTerms={false}
                showNotes={true}
              />
            </CollapsibleSection>

            {/* 8. Terms & Conditions */}
            <CollapsibleSection title="Terms & Conditions" expanded={!!accordion.termsConditions} onToggle={() => toggleSection('termsConditions')}>
              <PITermsConditionsSection
                purchaseOrder={purchaseOrder}
                onUpdate={handlePOUpdate}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            </CollapsibleSection>

            {/* 9. Prepared By / Signature */}
            <CollapsibleSection title="Prepared By" expanded={!!accordion.preparedBy} onToggle={() => toggleSection('preparedBy')}>
            <Box sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: alpha(theme.palette.primary.main, 0.02),
              mt: 0
            }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                Prepared By
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {purchaseOrder.prepared_by ? (
                  <Box sx={{ flex: 1, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                    <Box sx={{
                      fontFamily: "'Brush Script MT', cursive",
                      fontSize: '24px',
                      mb: 1
                    }}>
                      {purchaseOrder.prepared_by.name}
                    </Box>
                    <Typography variant="caption">
                      {purchaseOrder.prepared_by.position || 'Sales Representative'}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    No signature provided
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (!canEdit) return;
                    setSignaturePreview({
                      name: purchaseOrder.prepared_by?.name || '',
                      position: purchaseOrder.prepared_by?.position || 'Sales Representative'
                    });
                    setSignatureDialogOpen(true);
                  }}
                  sx={{
                    ml: 2,
                    opacity: canEdit ? 1 : 0.3,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'pointer' : 'not-allowed'
                  }}
                >
                  {purchaseOrder.prepared_by ? 'Change' : 'Add'}
                </Button>
              </Box>
            </Box>
            </CollapsibleSection>

            {/* Bottom action bar */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => setPrintDialogOpen(true)}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Preview & Print
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={canEdit ? handleSave : undefined}
                disabled={saving}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  opacity: canEdit ? 1 : 0.3,
                  pointerEvents: canEdit ? 'auto' : 'none',
                  cursor: canEdit ? 'pointer' : 'not-allowed',
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>

            {/* Print Preview Dialog */}
            <Dialog
              open={printDialogOpen}
              onClose={() => setPrintDialogOpen(false)}
              maxWidth="lg"
              fullWidth
              disableScrollLock
            >
              <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                fontWeight: 500,
                color: 'primary.main'
              }}>
                <Typography variant="h6">Proforma Invoice Preview</Typography>
                <IconButton onClick={() => setPrintDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <PIPrintable
                  ref={printComponentRef}
                  purchaseOrder={purchaseOrder}
                  customer={selectedCustomer}
                  company={company}
                  lineItems={lineItems.filter(item => item.description)}
                  formatCompanyAddress={formatCompanyAddress}
                  getCustomerName={getCustomerName}
                  showSummary={printOption === 'summary' || printOption === 'both'}
                  showLotBreakdown={printOption === 'lotBreakdown' || printOption === 'both'}
                />
              </DialogContent>
              <DialogActions sx={{ p: 3, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>Print Options</FormLabel>
                  <RadioGroup
                    row
                    value={printOption}
                    onChange={(e) => setPrintOption(e.target.value)}
                  >
                    <FormControlLabel value="summary" control={<Radio />} label="Summary Only" />
                    <FormControlLabel value="lotBreakdown" control={<Radio />} label="Lot Breakdown Only" />
                    <FormControlLabel value="both" control={<Radio />} label="Both" />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: 2 }}>
                  <Button onClick={() => setPrintDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
                    Print
                  </Button>
                </Box>
              </DialogActions>
            </Dialog>

            {/* Signature Dialog */}
            <Dialog
              open={signatureDialogOpen}
              onClose={() => setSignatureDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              disableScrollLock
            >
              <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
                Add Prepared By Signature
              </DialogTitle>
              <DialogContent sx={{ p: 3 }}>
                <TextField
                  label="Name"
                  fullWidth
                  margin="normal"
                  value={signaturePreview.name}
                  onChange={(e) => setSignaturePreview(prev => ({ ...prev, name: e.target.value }))}
                />
                <TextField
                  label="Position/Title"
                  fullWidth
                  margin="normal"
                  value={signaturePreview.position}
                  onChange={(e) => setSignaturePreview(prev => ({ ...prev, position: e.target.value }))}
                />
                <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                  <Typography variant="body2" gutterBottom>Preview:</Typography>
                  <Box sx={{ 
                    fontFamily: "'Brush Script MT', cursive", 
                    fontSize: '24px',
                    minHeight: '32px'
                  }}>
                    {signaturePreview.name || 'Your name will appear here'}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {signaturePreview.position || 'Sales Representative'}
      </Typography>
    </Box>
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button onClick={() => setSignatureDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveSignature}>Save Signature</Button>
              </DialogActions>
            </Dialog>
          </Container>
        </Box>
      </Fade>
    </LocalizationProvider>
  );
};

export default PICreation;